import { createApi } from '@reduxjs/toolkit/query/react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where 
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

const firebaseUsersBaseQuery = async (args: {
  type: string;
  groupName?: string;
  searchTerm?: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
}) => {
  try {
    const { type, ...params } = args;

    switch (type) {
      case 'fetchUsers': {
        const { groupName } = params;
        const usersRef = collection(db, 'sdsGroupUsers');
        let q: ReturnType<typeof query> = usersRef;

        if (groupName) {
          q = query(usersRef, where('groupName', '==', groupName));
        }

        const usersSnapshot = await getDocs(q);
        const users: unknown[] = [];
        usersSnapshot.forEach((docSnap) => {
          const userData = docSnap.data() as Record<string, unknown>;
          users.push({
            id: docSnap.id,
            ...userData
          });
        });

        const sortedUsers = (users as { userName?: string }[]).sort((a, b) =>
          (a.userName || '').localeCompare(b.userName || '')
        );

        return { data: sortedUsers };
      }

      case 'searchUsers': {
        const { searchTerm, groupName } = params;
        const usersRef = collection(db, 'sdsGroupUsers');
        let q: ReturnType<typeof query> = usersRef;

        if (groupName) {
          q = query(usersRef, where('groupName', '==', groupName));
        }

        const usersSnapshot = await getDocs(q);
        const users: { userName?: string; userEmail?: string; groupName?: string }[] = [];
        usersSnapshot.forEach((docSnap) => {
          const userData = docSnap.data() as Record<string, unknown>;
          users.push({
            id: docSnap.id,
            ...userData
          } as { id: string; userName?: string; userEmail?: string; groupName?: string });
        });

        let filteredUsers = users;
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.toLowerCase();
          filteredUsers = users.filter(user =>
            (user.userName || '').toLowerCase().includes(searchLower) ||
            (user.userEmail || '').toLowerCase().includes(searchLower) ||
            (user.groupName || '').toLowerCase().includes(searchLower)
          );
        }

        const sortedUsers = filteredUsers.sort((a, b) =>
          (a.userName || '').localeCompare(b.userName || '')
        );

        return { data: sortedUsers };
      }

      case 'fetchUserById': {
        const { userId } = params;
        const userDocRef = doc(db, 'sdsGroupUsers', userId!);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          return {
            data: {
              id: userDoc.id,
              ...userDoc.data()
            }
          };
        } else {
          throw new Error('User not found');
        }
      }

      case 'createUser': {
        const { userName, userEmail, groupName } = params;
        const usersRef = collection(db, 'sdsGroupUsers');
        const newUser = {
          userName,
          userEmail,
          groupName,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        const docRef = await addDoc(usersRef, newUser);
        return {
          data: {
            id: docRef.id,
            ...newUser
          }
        };
      }

      case 'updateUser': {
        const { userId, userName, userEmail, groupName } = params;
        const userDocRef = doc(db, 'sdsGroupUsers', userId!);
        const updateData: Record<string, unknown> = {
          updatedAt: new Date().toISOString()
        };
        
        if (userName !== undefined) updateData.userName = userName;
        if (userEmail !== undefined) updateData.userEmail = userEmail;
        if (groupName !== undefined) updateData.groupName = groupName;
        
        await updateDoc(userDocRef, updateData);
        
        const updatedDoc = await getDoc(userDocRef);
        return {
          data: {
            id: updatedDoc.id,
            ...updatedDoc.data()
          }
        };
      }

      case 'deleteUser': {
        const { userId } = params;
        const userDocRef = doc(db, 'sdsGroupUsers', userId!);
        await deleteDoc(userDocRef);
        return { data: { id: userId } };
      }

      default:
        throw new Error(`Unknown Firebase query type: ${type}`);
    }
  } catch (error) {
    return { error: { message: (error as Error).message } };
  }
};

export const usersApiSlice = createApi({
  reducerPath: 'usersApi',
  baseQuery: firebaseUsersBaseQuery as any,
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    fetchUsers: builder.query({
      query: ({ groupName }: { groupName?: string } = {}) => ({
        type: 'fetchUsers',
        groupName,
      }),
      providesTags: ['Users'],
    }),

    searchUsers: builder.query({
      query: ({ searchTerm, groupName }: { searchTerm?: string; groupName?: string } = {}) => ({
        type: 'searchUsers',
        searchTerm,
        groupName,
      }),
      providesTags: ['Users'],
    }),

    fetchUserById: builder.query({
      query: ({ userId }: { userId: string }) => ({
        type: 'fetchUserById',
        userId,
      }),
      providesTags: (_result, _error, { userId }) => [{ type: 'Users', id: userId }],
    }),

    createUser: builder.mutation({
      query: ({ userName, userEmail, groupName }: { userName: string; userEmail: string; groupName: string }) => ({
        type: 'createUser',
        userName,
        userEmail,
        groupName,
      }),
      invalidatesTags: ['Users'],
    }),

    updateUser: builder.mutation({
      query: ({ userId, userName, userEmail, groupName }: { userId: string; userName?: string; userEmail?: string; groupName?: string }) => ({
        type: 'updateUser',
        userId,
        userName,
        userEmail,
        groupName,
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'Users', id: userId }, 'Users'],
    }),

    deleteUser: builder.mutation({
      query: ({ userId }: { userId: string }) => ({
        type: 'deleteUser',
        userId,
      }),
      invalidatesTags: ['Users'],
    }),
  }),
});

export const {
  useFetchUsersQuery,
  useFetchUserByIdQuery,
  useSearchUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = usersApiSlice;
